import { hasCmeAiAssistCapability } from '../../Common/Utils/CmeCapabilityUtils'

/**
 * Inputs that decide whether the TelSys AI Assist toggle can be changed.
 */
export type AiAssistToggleState = {
    readonly viewOnly: boolean
    readonly isRootCMEAdmin: boolean
    readonly cmeCapabilities: Nullable<number>
    /** Whether AI Assist is on in the saved TelSys, so a toggle-off can be undone before saving. */
    readonly isAiAssistSaved: boolean
}

/**
 * Whether the CME's missing AI Assist capability is what stops the toggle being switched on.
 *
 * @param state The view mode, admin level, CME capabilities and saved toggle value.
 * @returns True when the toggle is locked only because the CME lacks AI Assist.
 */
export const isAiAssistLockedByCme = ({
    viewOnly,
    isRootCMEAdmin,
    cmeCapabilities,
    isAiAssistSaved
}: AiAssistToggleState): boolean =>
    !viewOnly && isRootCMEAdmin && !isAiAssistSaved && !hasCmeAiAssistCapability(cmeCapabilities)

/**
 * Whether the TelSys AI Assist toggle is disabled.
 *
 * Only a Root CME admin can change AI Assist. When the owning CME lacks the AI Assist capability it can only be
 * switched off, never on, whoever the admin is (APD-13052); the backend rejects turning it on. A TelSys saved with
 * AI Assist on can still be switched back on in the same edit.
 *
 * @param state The view mode, admin level, CME capabilities and saved toggle value.
 * @returns True when the toggle must be disabled.
 */
export const isAiAssistToggleDisabled = (state: AiAssistToggleState): boolean =>
    state.viewOnly || !state.isRootCMEAdmin || isAiAssistLockedByCme(state)
